import { createContext, useContext, useState, useCallback, useRef } from 'react';
import { apiRequest } from '../utils/apiRequest';

const DataContext = createContext(null);

const STALE_TIME = 5 * 60 * 1000; // 5 minutes

export const DataProvider = ({ children }) => {
  const [providers, setProviders] = useState([]);
  const [popularCategories, setPopularCategories] = useState([]);
  // Storefront navigation tabs, independent of the synced Hot Recharge categories.
  const [navSections, setNavSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const lastFetchedAt = useRef(null);

  const fetchData = useCallback(async (force = false) => {
    if (!force && lastFetchedAt.current && Date.now() - lastFetchedAt.current < STALE_TIME) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const [providersRes, categoriesRes, navRes] = await Promise.allSettled([
        apiRequest({ method: 'get', url: '/providers', suppressNotifications: true }),
        apiRequest({ method: 'get', url: '/popular_categories?limit=8', suppressNotifications: true }),
        apiRequest({ method: 'get', url: '/nav_sections', suppressNotifications: true }),
      ]);

      // Only ever store arrays: an API error body (e.g. { error: ... }) is truthy,
      // and letting it into state crashes every consumer that calls .map on it.
      if (providersRes.status === 'fulfilled') {
        const data = providersRes.value.data;
        if (Array.isArray(data)) setProviders(data);
      }
      if (categoriesRes.status === 'fulfilled') {
        const data = categoriesRes.value.data;
        const cats = Array.isArray(data?.categories) ? data.categories : Array.isArray(data) ? data : null;
        if (cats) setPopularCategories(cats);
      }

      if (navRes.status === 'fulfilled') {
        const sections = navRes.value.data?.sections;
        if (Array.isArray(sections)) setNavSections(sections);
      }

      // The nav tabs are an enhancement: if only they fail the rest of the home
      // page must still render, so they are left out of the error check.
      const errors = [providersRes, categoriesRes].filter(r => r.status === 'rejected');
      if (errors.length > 0) {
        setError('Site is currently under maintenance. Please check back soon!');
      }

      lastFetchedAt.current = Date.now();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  return (
    <DataContext.Provider value={{
      providers, popularCategories, navSections,
      loading, error, fetchData
    }}>
      {children}
    </DataContext.Provider>
  );
};

export const useData = () => useContext(DataContext);
